import { useEffect, useState, useRef } from "react";

interface PreloadOptions {
  images?: string[];
  audios?: string[];
  animations?: string[];
}

const usePreloader = ({
  images = [],
  audios = [],
  animations = [],
}: PreloadOptions) => {
  const [progress, setProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const totalAssets = images.length + audios.length + animations.length;
  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const loadedCount = useRef(0);

  useEffect(() => {
    if (totalAssets === 0) {
      setIsLoaded(true);
      return;
    }

    const updateProgress = () => {
      loadedCount.current++;
      const newProgress = Math.min(
        Math.round((loadedCount.current / totalAssets) * 100),
        100
      );
      setProgress(newProgress);

      if (loadedCount.current >= totalAssets) {
        setIsLoaded(true);
      }
    };

    images.forEach((src) => {
      const img = new Image();
      img.onload = updateProgress;
      img.onerror = updateProgress;
      img.src = src;
    });

    if (audioRefs.current.length === 0) {
      audioRefs.current = audios.map((src) => {
        const audio = new Audio();
        audio.src = src;
        audio.load();
        audio.oncanplaythrough = updateProgress;
        audio.onerror = updateProgress;
        return audio;
      });
    } else {
      audios.forEach(updateProgress);
    }

    Promise.all(
      animations.map((src) =>
        fetch(src)
          .then((res) => res.json())
          .then(updateProgress)
          .catch(updateProgress)
      )
    );
  }, []);

  return { progress, isLoaded };
};

export default usePreloader;
